import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Button, Tag, Select as AntSelect, Pagination } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { Bar } from 'react-chartjs-2';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  DAYS_WINDOW_OPTIONS,
  getPipelineHealthSla,
  getPipelineHealthDomains
} from '../../../../api/pipelineHealthApi';
import { formatDomain, DOMAIN_COLORS, FALLBACK_COLOR } from '../pipelineHealthUtils';
import { thousand } from '../../../../functions';

// Colour the per-domain SLA percentage. Green when fully on, amber for a
// near-miss, red below a soft threshold.
function slaPercentColor(pct) {
  if (pct == null) return '#9ca3af';
  if (pct >= 95) return '#16a34a';
  if (pct >= 80) return '#f59e0b';
  return '#dc2626';
}

// Bar colour for the freshness chart — green when the SLA was met, red when
// it was breached. Easier to scan than a single colour with a reference line.
function freshnessBarColor(slaMet) {
  return slaMet ? '#16a34a' : '#dc2626';
}

function SlaCompliance() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [daysWindow, setDaysWindow] = useState(7);
  const [domains, setDomains] = useState([]);
  const [domainFilter, setDomainFilter] = useState('');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Domains feed the optional domain dropdown.
  useEffect(() => {
    getPipelineHealthDomains(true)
      .then((res) => setDomains(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, []);

  const load = () => {
    setLoading(true);
    getPipelineHealthSla(daysWindow, domainFilter)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [daysWindow, domainFilter]);

  const daysOptions = DAYS_WINDOW_OPTIONS.map((d) => ({
    value: d,
    label: t(`pipeline_health_days_${d}`)
  }));
  const domainOptions = [
    { value: '', label: t('pipeline_health_all_domains') },
    ...domains.map((d) => ({ value: d, label: formatDomain(d) }))
  ];

  // ---- Freshness chart -------------------------------------------------

  const freshnessChart = useMemo(() => {
    const rows = data?.entities || [];
    if (rows.length === 0) return null;
    // Sort by descending ratio so the worst offenders sit at the top.
    const sorted = [...rows].sort((a, b) => (Number(b.ratio) || 0) - (Number(a.ratio) || 0));
    return {
      labels: sorted.map((r) => r.entityName),
      datasets: [
        {
          label: t('pipeline_health_sla_hours_since_success'),
          data: sorted.map((r) => r.hoursSinceSuccess ?? 0),
          backgroundColor: sorted.map((r) => freshnessBarColor(r.slaMet)),
          borderColor: sorted.map((r) => freshnessBarColor(r.slaMet)),
          // Tag each bar with its expected frequency so the tooltip can show
          // it without another data lookup.
          raw: sorted
        }
      ]
    };
  }, [data, t]);

  const freshnessOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const row = freshnessChart?.datasets?.[0]?.raw?.[ctx.dataIndex];
            if (!row) return '';
            return [
              `${t('pipeline_health_sla_hours_since_success')}: ${row.hoursSinceSuccess ?? '—'}`,
              `${t('pipeline_health_sla_expected_freq')}: ${row.expectedFrequencyHrs ?? '—'}h`,
              `${t('pipeline_health_sla_ratio')}: ${row.ratio == null ? '—' : Number(row.ratio).toFixed(2)}`,
              row.slaMet ? t('pipeline_health_sla_met') : t('pipeline_health_sla_breach')
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: t('pipeline_health_sla_hours_since_success') }
      },
      y: { ticks: { font: { size: 10 } } }
    }
  }), [freshnessChart, t]);

  // Chart height — let each entity row have ~22px of vertical space.
  const freshnessHeight = Math.max(280, ((data?.entities || []).length) * 22 + 60);

  // ---- Detail table ----------------------------------------------------

  const columns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    { title: t('domain'), dataIndex: 'domain', key: 'domain', width: 140, render: formatDomain },
    {
      title: t('pipeline_health_sla_hours_since_success'),
      dataIndex: 'hoursSinceSuccess',
      key: 'hoursSinceSuccess',
      width: 130,
      align: 'right',
      render: (v) => (v == null ? '—' : `${thousand(v)}h`)
    },
    {
      title: t('pipeline_health_sla_expected_freq'),
      dataIndex: 'expectedFrequencyHrs',
      key: 'expectedFrequencyHrs',
      width: 130,
      align: 'right',
      render: (v) => (v == null ? '—' : `${thousand(v)}h`)
    },
    {
      title: t('pipeline_health_sla_ratio'),
      dataIndex: 'ratio',
      key: 'ratio',
      width: 90,
      align: 'right',
      render: (v) => (v == null ? '—' : Number(v).toFixed(2))
    },
    {
      title: t('pipeline_health_sla_status'),
      dataIndex: 'slaMet',
      key: 'slaMet',
      width: 100,
      render: (v) => (
        v
          ? <Tag color="green">{t('pipeline_health_sla_met')}</Tag>
          : <Tag color="red">{t('pipeline_health_sla_breach')}</Tag>
      )
    }
  ];

  const slaBreachColumns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    { title: t('date'), dataIndex: 'breachDate', key: 'breachDate', render: (d) => (d ? dayjs(d).format('MMM DD, YYYY') : '—') },
    { title: t('reason'), dataIndex: 'breachReason', key: 'breachReason', render: (r) => (r ?? 'N/A') },
  ];

  // ----------------------------------------------------------------------

  const byDomain = data?.byDomain || [];
  const entities = data?.entities || [];
  const slaBreach = data?.slaBreachEntities || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_days_window')}:</span>
        <AntSelect
          value={daysWindow}
          disabled={loading}
          onChange={setDaysWindow}
          style={{ width: 150 }}
          options={daysOptions}
        />
        <span className="text-sm text-gray-600">{t('domain')}:</span>
        <AntSelect
          value={domainFilter}
          disabled={loading}
          onChange={setDomainFilter}
          style={{ minWidth: 160 }}
          options={domainOptions}
        />
        {data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        {data?.datasetStart && !data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetStart).add(30, 'days').format('MMM DD, YYYY')}
          </span>
        )}
        {!data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetEnd).add(-30, 'days').format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        <Button
          className="ml-auto"
          type="primary"
          onClick={load}
          loading={loading}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4">

          {/* Per-domain SLA cards + total */}
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${Math.max(byDomain.length + 1, 1)}, minmax(160px, 1fr))` }}
          >
            {byDomain.map((d) => (
              <Spin spinning={loading}>
                <div
                  key={d.domain}
                  className="bg-white rounded shadow-sm p-3 text-center"
                  style={{ borderTop: `4px solid ${DOMAIN_COLORS[d.domain] || FALLBACK_COLOR}` }}
                >
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    {t('pipeline_health_sla')} — {formatDomain(d.domain)}
                  </div>
                  <div className="text-3xl font-bold" style={{ color: slaPercentColor(d.slaPercent) }}>
                    {d.slaPercent}%
                  </div>
                  <div className="text-xs text-gray-400">
                    {thousand(d.slaMetCount)} / {thousand(d.slaTotal)} {t('pipeline_health_sla_entities_short')}
                  </div>
                </div>
              </Spin>
            ))}
            <Spin spinning={loading}>
              <div className="bg-white rounded shadow-sm p-3 text-center" style={{ borderTop: '4px solid #475569' }}>
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  {t('pipeline_health_sla_total')}
                </div>
                <div className="text-3xl font-bold">{data.totalRecurring ?? 0}</div>
                <div className="text-xs text-gray-400">{t('pipeline_health_sla_recurring_active')}</div>
              </div>
            </Spin>
          </div>

          {/* Freshness chart */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_sla_freshness')}</div>
            {!freshnessChart ? (
              <Empty description={t('no_data')} />
            ) : (
              <>
                <Spin spinning={loading}>
                  <div style={{ height: freshnessHeight }}>
                    <Bar data={freshnessChart} options={freshnessOptions} />
                  </div>
                </Spin>
                {/* Legend */}
                <div className="flex gap-3 mt-2 text-[10px] text-gray-500 items-center">
                  <span className="flex items-center gap-1">
                    <span style={{ background: freshnessBarColor(true), width: 10, height: 10, borderRadius: 2, display: 'inline-block' }} />
                    {t('pipeline_health_sla_met')}
                  </span>
                  <span className="flex items-center gap-1">
                    <span style={{ background: freshnessBarColor(false), width: 10, height: 10, borderRadius: 2, display: 'inline-block' }} />
                    {t('pipeline_health_sla_breach')}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Detail table */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_sla_detail')}</div>
            {entities.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <>
                <Table
                  loading={loading}
                  size="small"
                  rowKey={(row, idx) => `${row.domain}-${row.entityName}-${idx}`}
                  columns={columns}
                  dataSource={entities}
                  pagination={{ hideOnSinglePage: true }}
                />
              </>
            )}
          </div>

          {/* SLA Breach table */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_sla_breach_table')}</div>
            {slaBreach.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <>
                <Table
                  size="small"
                  loading={loading}
                  rowKey={(row, idx) => `${row.entityName}-${row.breachDate}-${idx}`}
                  columns={slaBreachColumns}
                  dataSource={slaBreach}
                  pagination={{ hideOnSinglePage: true }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SlaCompliance;
